import React from 'react';
import { injectIntl } from 'react-intl';
import { makeStyles } from 'tss-react/mui';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { slidingToggleButtonNoBorder, slidingToggleGroupNoBorder } from '../../../SlidingToggleButton/noBorderStyles';

const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'center'
  },
  toggleGroup: {
    position: 'relative',
    backgroundColor: '#f3f5fb',
    borderRadius: '8px !important',
    padding: 6,
    zIndex: 0,
    // Equal columns as wide as the longest label, so no label has to wrap and the indicator fits one cell.
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridAutoColumns: '1fr',
    ...slidingToggleGroupNoBorder
  },
  toggleButton: {
    ...slidingToggleButtonNoBorder,
    borderRadius: '8px !important',
    padding: '0 20px',
    fontWeight: 'normal',
    fontSize: 14,
    height: 36,
    minWidth: 90,
    whiteSpace: 'nowrap',
    color: '#616161',
    textTransform: 'none',
    zIndex: 2,
    backgroundColor: 'transparent',
    transition: 'color 0.25s ease !important',
    WebkitTapHighlightColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(57, 102, 248, 0.04)',
      border: 'none !important',
      borderColor: 'transparent !important',
      boxShadow: 'none'
    }
  },
  selected: {
    color: '#ffffff !important',
    backgroundColor: 'transparent !important',
    '&:hover': {
      backgroundColor: 'transparent !important'
    }
  },
  // Tighter buttons, for switches with more than two options.
  compact: {
    height: 32,
    minWidth: 0,
    padding: '0 14px',
    fontSize: 13
  },
  slidingIndicator: {
    position: 'absolute',
    top: 6,
    bottom: 6,
    left: 6,
    width: 'calc((100% - 12px) / var(--switch-count, 2))',
    backgroundColor: '#3966f8',
    borderRadius: 8,
    boxShadow: '0 4px 10px rgba(57, 102, 248, 0.2)',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 1
  }
}));

/**
 * The sliding two-way switch. `option.label` is shown as given (a string or node);
 * `option.testId`, when set, becomes the button's `data-testid`, and `option.ariaLabel`
 * its accessible name. Any number of options; `compact` tightens the buttons for 3+.
 */
export const SlidingSwitch = ({ current, options, onAction, ariaLabel = 'version toggle', compact = false }) => {
  const { classes } = useStyles();

  const handleChange = next => {
    if (next && next !== current) {
      onAction(next);
    }
  };

  const currentIndex = options.findIndex(option => option.value === current);

  return (
    <div className={classes.root}>
      <ToggleButtonGroup
        value={current}
        exclusive
        onChange={(e, next) => handleChange(next)}
        aria-label={ariaLabel}
        classes={{ root: classes.toggleGroup }}
      >
        <div
          className={classes.slidingIndicator}
          style={{
            '--switch-count': options.length,
            transform: `translateX(${currentIndex === -1 ? 0 : currentIndex * 100}%)`
          }}
        />
        {options.map(option => (
          <ToggleButton
            value={option.value}
            classes={{
              root: `${classes.toggleButton}${compact ? ` ${classes.compact}` : ''}`,
              selected: classes.selected
            }}
            key={option.value}
            aria-label={option.ariaLabel}
            data-testid={option.testId}
          >
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </div>
  );
};

/** The drawers' version: `option.label` is a message id. */
const SuggestionTypeSwitchButton = ({ intl, options, ...rest }) => (
  <SlidingSwitch
    options={options.map(option => ({ ...option, label: intl.formatMessage({ id: option.label }) }))}
    {...rest}
  />
);

export default injectIntl(SuggestionTypeSwitchButton);
